import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FacebookShareButton } from 'react-share';
import { useSelector } from 'react-redux';

import WebGalleryFavoritePhotoButtonContainer from '../../containers/WebGalleryFavoritePhotoButtonContainer';
import {
  allowsFreeDownloads,
  allowsPhotoPurchase,
  socialShareEnabled,
} from '../../../../util/album';
import PrinterIcon from '../../../../components/icons/PrinterIcon';
import DownloadIcon from '../../../../components/icons/DownloadIcon';
import { WEB_GALLERY_DOWNLOAD_MODAL } from '../WebGalleryDownloadModal/WebGalleryDownloadModal';
import { canReportPhotos } from '../../../../util/product-permissions';
import ReportPhotoButton from '../../../../components/global/ReportPhotoButton/ReportPhotoButton';
import WebGalleryHeaderCart from '../WebGalleryHeaderCart';
import {
  SHARE_BLOCK_MODAL,
  WEB_GALLERY_CHECKOUT,
} from '../../../../constants/modals';
import FacebookSquareIcon from '../../../../components/icons/FacebookSquareIcon';
import getConfig from '../../../../util/config';
import {
  ANALYTIC_EVENTS,
  trackWebGalleryEvent,
} from '../../utilities/analytics';
import { removeHashes } from '../../../../util/text';
import useOpenModal from '../../../../hooks/useOpenModal';
import Button from '../../../../components/global/Button';
import WarningIcon from '../../../../components/icons/WarningIcon';
import { MEMBERSHIPS_PURCHASING_OPTIONS_MODAL } from '../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions';
import { isPhotoDownloadPurchased } from '../../utilities/web-gallery-cart-utils';
import FavoritesIcon from '../../../../components/icons/FavoritesIcon';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_favorite: PropTypes.bool,
  }).isRequired,
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  onFavoriteToggled: PropTypes.func,
  forceAllowHiResDownloads: PropTypes.bool,
};

const WebGalleryPhotoViewerActions = ({
  photo,
  album,
  onFavoriteToggled,
  forceAllowHiResDownloads,
}) => {
  const openModal = useOpenModal();
  const hashCode = removeHashes(album.hash_code);
  const { selectedDownloads, selectedPrints } = useSelector(
    state => state.webGalleryCart,
  );

  return (
    // Use photo.id as key to force a re-render of these components.
    // For some reason, they were not being re-rendered.
    <Fragment key={photo?.id}>
      <WebGalleryFavoritePhotoButtonContainer
        photo={photo}
        className="btn-reset dropdown-item"
      >
        {({ onFavoriteToggle }) => (
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={() => {
              onFavoriteToggle();
              onFavoriteToggled?.();
            }}
          >
            <FavoritesIcon color="#fff" size={18} className="mr-1" />{' '}
            {photo.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
          </Button>
        )}
      </WebGalleryFavoritePhotoButtonContainer>
      {allowsPhotoPurchase(album, true) && (
        <Button
          btnStyle="reset"
          className="dropdown-item"
          onClick={() =>
            openModal(MEMBERSHIPS_PURCHASING_OPTIONS_MODAL, {
              album,
              photo,
              isEntitled:
                forceAllowHiResDownloads ||
                isPhotoDownloadPurchased(album, photo) ||
                allowsFreeDownloads(album),
            })
          }
        >
          <PrinterIcon color="#fff" size={18} className="mr-1" /> Prints
        </Button>
      )}
      <Button
        btnStyle="reset"
        className="dropdown-item"
        onClick={() =>
          openModal(WEB_GALLERY_DOWNLOAD_MODAL, {
            album,
            photo,
          })
        }
      >
        <DownloadIcon color="#fff" size={18} className="mr-1" /> Download
      </Button>
      {canReportPhotos(album) && (
        <ReportPhotoButton
          btnStyle="reset"
          className="dropdown-item"
          albumId={album.id}
          photoId={photo.id}
        >
          <WarningIcon color="#fff" size={18} className="mr-1" /> Report Photo{' '}
        </ReportPhotoButton>
      )}
      <WebGalleryHeaderCart
        selectedDownloads={selectedDownloads}
        selectedPrints={selectedPrints}
        checkout={() => openModal(WEB_GALLERY_CHECKOUT)}
        cartColor="#ffffff"
        className="dropdown-item"
        displayTextClassName="ml-1"
        displayText="Checkout"
        iconId="photo-viewer-actions-cart-icon"
      />
      {socialShareEnabled(album) &&
        (photo.share_blocked ? (
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={() => openModal(SHARE_BLOCK_MODAL)}
          >
            <FacebookSquareIcon color="#fff" size={18} className="mr-1" />
            Share on Facebook
          </Button>
        ) : (
          <FacebookShareButton
            className="btn-reset dropdown-item"
            url={`${getConfig().app.baseUrl}/p/${photo.id}`}
            hashtag={hashCode && `#${hashCode}`}
            beforeOnClick={() => {
              trackWebGalleryEvent(
                ANALYTIC_EVENTS.PHOTO_BROWSER_DID_TAP_SHARE_ON_FB,
              );
            }}
          >
            <FacebookSquareIcon color="#fff" size={18} className="mr-1" /> Share
            on Facebook
          </FacebookShareButton>
        ))}
    </Fragment>
  );
};

WebGalleryPhotoViewerActions.propTypes = propTypes;

export default WebGalleryPhotoViewerActions;
